import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { getPriestPortal, savePriestPortal } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Shield } from "lucide-react";
import { useT } from "@/lib/t";

/**
 * Priest self-serve portal for the Homily Prep digest.
 * Reached via a signed magic-link `/priest-portal/{priest_id}?token={hmac}`.
 * No session, no login — the HMAC token is the credential.
 *
 * Copy is localized via ``useT`` so a bilingual chancery assistant can
 * drive the flow in Spanish via the site language switcher, even before
 * the priest changes their own preference.
 */
export default function PriestPortal() {
  const { priestId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const t = useT();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [priest, setPriest] = useState(null);
  const [optIn, setOptIn] = useState(false);
  const [lang, setLang] = useState("en");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await getPriestPortal(priestId, token);
        setPriest(r.priest);
        setOptIn(!!r.opt_in);
        setLang(r.lang || "en");
      } catch (e) {
        setError(e?.response?.status === 403
          ? t("priest_portal.link_invalid")
          : (e?.response?.data?.detail || t("priest_portal.generic_fail")));
      } finally { setLoading(false); }
    })();
  }, [priestId, token]);

  const save = async () => {
    setSaving(true);
    try {
      await savePriestPortal(priestId, { token, opt_in: optIn, lang });
      setSavedAt(new Date());
      toast.success(t("priest_portal.saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("priest_portal.generic_fail"));
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-ccc-paper" data-testid="priest-portal-page">
      <header className="bg-ccc-plum text-ccc-paper py-6">
        <div className="max-w-2xl mx-auto px-6">
          <div className="eyebrow eyebrow-light">Colorado Catholic Conference</div>
          <div className="font-serif text-2xl mt-1">{t("priest_portal.title")}</div>
          <div className="text-ccc-paper/80 mt-1" style={{ fontSize: "15px" }}>{t("priest_portal.subtitle")}</div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-10">
        {loading && <div className="text-ccc-muted" style={{ fontSize: "15px" }}>{t("common.loading")}</div>}

        {error && (
          <div className="bg-white border border-ccc-rule border-l-4 border-l-red-500 p-8 text-center" data-testid="portal-error">
            <XCircle className="mx-auto text-red-600" size={40}/>
            <div className="font-serif text-2xl text-ccc-plum mt-3">{error}</div>
            <p className="text-ccc-body mt-2" style={{ fontSize: "16px" }}>{t("priest_portal.link_invalid_body")}</p>
          </div>
        )}

        {priest && !error && (
          <>
            <div className="bg-white border border-ccc-rule p-6 mb-6" data-testid="portal-priest-card">
              <div className="eyebrow" style={{ fontSize: "12px" }}>{t("priest_portal.welcome")}</div>
              <div className="font-serif text-3xl text-ccc-plum mt-1">{priest.name}</div>
              <div className="text-ccc-body" style={{ fontSize: "15px" }}>{priest.parish}</div>
              <div className="text-ccc-muted" style={{ fontSize: "13px" }}>{priest.diocese}</div>
              <div className="mt-3 inline-flex items-center gap-2 text-ccc-muted" style={{ fontSize: "13px" }}>
                <Shield size={12}/>
                {optIn ? t("priest_portal.currently_subscribed") : t("priest_portal.currently_unsubscribed")}
              </div>
            </div>

            <div className="bg-white border border-ccc-rule p-6" data-testid="portal-form">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={optIn}
                  onChange={(e) => setOptIn(e.target.checked)}
                  data-testid="portal-opt-in"
                  className="mt-1 accent-ccc-plum"
                />
                <span>
                  <span className="font-serif text-ccc-ink" style={{ fontSize: "18px" }}>{t("priest_portal.opt_in_label")}</span>
                  <span className="block text-ccc-body mt-1" style={{ fontSize: "14px" }}>{t("priest_portal.opt_in_hint")}</span>
                </span>
              </label>

              <div className="mt-6">
                <div className="eyebrow mb-3">{t("priest_portal.lang_label")}</div>
                <div className="flex gap-3">
                  <label className={`flex-1 border p-3 cursor-pointer transition-colors ${lang === "en" ? "border-ccc-plum bg-ccc-paper" : "border-ccc-rule hover:border-ccc-plum"}`}>
                    <input type="radio" checked={lang === "en"} onChange={() => setLang("en")}
                      data-testid="portal-lang-en"
                      className="mr-2 accent-ccc-plum"/>
                    <span style={{ fontSize: "16px" }}>{t("priest_portal.lang_en")}</span>
                  </label>
                  <label className={`flex-1 border p-3 cursor-pointer transition-colors ${lang === "es" ? "border-ccc-plum bg-ccc-paper" : "border-ccc-rule hover:border-ccc-plum"}`}>
                    <input type="radio" checked={lang === "es"} onChange={() => setLang("es")}
                      data-testid="portal-lang-es"
                      className="mr-2 accent-ccc-plum"/>
                    <span style={{ fontSize: "16px" }}>{t("priest_portal.lang_es")}</span>
                  </label>
                </div>
              </div>

              <button
                onClick={save}
                disabled={saving}
                data-testid="portal-save"
                className="mt-6 w-full bg-ccc-plum hover:bg-ccc-plum-deep text-ccc-paper py-3 uppercase tracking-[0.08em] font-semibold disabled:opacity-40"
                style={{ fontSize: "15px" }}
              >
                {saving ? t("priest_portal.saving") : t("priest_portal.save")}
              </button>

              {savedAt && (
                <div className="mt-4 inline-flex items-center gap-2 text-ccc-plum" data-testid="portal-saved" style={{ fontSize: "14px" }}>
                  <CheckCircle2 size={14}/> {t("priest_portal.saved")}
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
